// Need to use the React-specific entry point to import createApi
import {
  createApi,
  fetchBaseQuery,
  QueryStatus,
} from '@reduxjs/toolkit/query/react'
import { API_URL } from '../../constants'
import type {
  ActivateWealthManagerResponse,
  AddProductAnswersDto,
  AdminFundDepositDto,
  AffiliatePartnerCreateCommand,
  AffiliatePartnerDto,
  AffiliateStatsDto,
  Auth0InviteMemberResponse,
  Auth0VerificationEmailResponse,
  BackupActionResultDto,
  BackupCaseResolveCommandDto,
  BackupCustomerExitCommandDto,
  BackupPepConfirmCommandDto,
  BackupRestrictionDecisionCommandDto,
  BackupSofRequestCloseCommandDto,
  BackupStrFiledCommandDto,
  BackupTierChangeCommandDto,
  BankAccountCreateCommand,
  BankAccountDto,
  BankAccountUpdateCommand,
  BrokerPerformanceDto,
  CloseMyAccountCommand,
  CollectOkxBalanceDataResponse,
  ComplianceInvestorDto,
  ComplianceOnboardingAnswersDto,
  ComplianceOnboardingStateDto,
  CreateFundDepositCommand,
  DeclareKycResidenceCommand,
  KycResidenceEligibilityDto,
  CreateOkxAccountDto,
  CurrencyCreateCommand,
  CurrencyDto,
  CurrencyToOffRampDto,
  CustomerInsightsDto,
  CustomerInsightsFilterDto,
  DepositDto,
  ExchangeRateAllDto,
  ExchangeRatePairDto,
  CreateCommand as FeeStructureCreateCommand,
  FeeStructureDto,
  FundDepositInvoiceDto,
  FundPerformanceDto,
  InvestorFundPerformanceDto,
  InviteCommand,
  InviteStaffMemberCommand,
  InviteStaffMemberResponse,
  InviteWealthManagerResponse,
  KrakenClosedOrdersDto,
  KycStatus,
  MfaChallengeCommand,
  MfaChallengeDto,
  MfaDisableCommand,
  MfaEnrollDto,
  MfaVerifyCommand,
  MfaVerifyDto,
  NetworkCreateCommand,
  NetworkDto,
  NetworkUpdateCommand,
  OkxAccountDto,
  OkxBalanceHistoryDto,
  OkxPortfolioMetricsDto,
  ProvisionOkxSubAccountResponse,
  LatestRescoreRunDto,
  RescorePreviewDto,
  RescoreResultDto,
  RescoreRunStatusDto,
  ResetInvestorComplianceCommandDto,
  ResetInvestorComplianceResultDto,
  ResyncResultDto,
  RiskTier,
  SendVeliRemindersResult,
  SumsubAccessTokenDto,
  SuspendAccountCommand,
  SwapActivityDto,
  SwapCreateCommandModel,
  SwapDto,
  SwapInvoiceDto,
  SwapUpdateCommand,
  SwapUpdateStatusCommand,
  TeamMemberDto,
  ThorchainSwapCreateCommandModel,
  ThorchainSwapDto,
  TransferActivityDto,
  TransferDto,
  TransferQuotationDto,
  UpdateAffiliatePartnerCommandModel,
  UpdateCurrencyCommandModel,
  UpdateFundDepositCommandModel,
  UpdateFundPerformanceCommand,
  UpdateInvestorAnswersResultDto,
  UpdateKycStatusCommand,
  UpdateOkxAccountDto,
  UpdateUserCommandModel,
  UserCreateCommand,
  UserDto,
  VeliActivationFunnel,
  WalletCreateCommand,
  WalletDto,
  WalletUpdateCommand,
} from '../../types/coreApi-types'
import type {
  FinanstilsynetCustomerBreakdownRowDto,
  FinanstilsynetFilters,
  FinanstilsynetSamplingListRowDto,
} from '../../types/internal-types'
import type { RootState } from '../store'

// The user-deposit endpoint takes the investor via the URL and defaults
// transferredAt server-side, so the body is just these fields of the generated
// command. Derived here (not hand-added to coreApi-types.ts) so
// `npm run gen:types` can't wipe it.
type CreateUserFundDepositCommandModel = Pick<
  CreateFundDepositCommand,
  | 'investmentPackage'
  | 'amountIn'
  | 'currencyInId'
  | 'fromBankAccountId'
  | 'fromWalletId'
>

type StartImpersonationRequest = {
  targetUserId: string
  reason: string
  totpCode: string
}

type StartImpersonationResponse = {
  token: string
  expiresAtUtc: string
  targetUserId: string
  targetRole: 'Individual' | 'Organization' | 'Member'
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, api) => {
    const state = api.getState() as RootState

    const token = state.session.token
    const otacToken = state.session.otacToken
    const impersonationToken = state.session.impersonationToken

    headers.set('Authorization', `Bearer ${token}`)
    if (otacToken) {
      headers.set('OneTimeAccessToken', otacToken)
    }
    if (impersonationToken) {
      headers.set('ImpersonationToken', impersonationToken)
    }
    return headers
  },
})

// Base query for public endpoints that don't require authentication
const publicBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
})

// Define a service using a base URL and expected endpoints
// A Veli client's coarse migration status as a wealth manager sees it (issue #83, ADR-0006).
// Mirrors the API's `MigrationStatus`; the API serialises enums as strings. Deliberately coarse —
// the suspicion states collapse into `OnboardingInProgress` server-side (tipping-off, LBK §38), and
// `Rejected` is a bare verdict. Defined here (not in generated coreApi-types.ts) so `gen:types` can't
// wipe it before the endpoint lands in the OpenAPI spool.
export type MigrationStatus =
  | 'NotActivated'
  | 'Activated'
  | 'OnboardingInProgress'
  | 'Onboarded'
  | 'Rejected'

// One client in a wealth manager's own book: identity + status only, never a balance (ADR-0006 §6).
// `veliUserId` is the bare, non-secret lookup key (ADR-0004 §3) used to target the per-client remind
// action on not-activated rows (issue #85). `email` is identity too: the Veli import only requires an
// email, so `name` can be blank and the email is then the only way to tell the client apart (ADR-0006
// amendment). `activationLink` is the bare-id activation URL (ADR-0004 §3) the WM can copy and forward —
// present only on not-yet-activated clients (ADR-0006 §4); the server withholds it for everyone else, so
// an absent link is how the screen knows not to offer the copy affordance.
export interface WealthManagerClientDto {
  veliUserId: string
  name: string
  email: string
  status: MigrationStatus
  activationLink?: string | null
}

export interface WealthManagerBookDto {
  clients: WealthManagerClientDto[]
}

// The record of who a per-client reminder reached — the client's mailbox, never the WM's (issue #85).
export interface RemindWealthManagerClientResult {
  veliUserId: string
  email: string
}

// The client a wealth manager removed from their own book, and when (ADR-0016).
export interface UnlinkWealthManagerClientResult {
  veliUserId: string
  unlinkedAt: string
}

// A wealth manager's own derived lifecycle state — Seeded → Invited → Active — from the Auth0 login and
// the signed-agreement marker (CONTEXT.md, ADR-0006). "Active" is reserved for a WM here; a *client*'s
// platform entry is "Activated". Hand-defined alongside the other Veli admin DTOs until the endpoint
// lands in the OpenAPI spool (gen:types).
export type WealthManagerLifecycleStatus = 'Seeded' | 'Invited' | 'Active'

// One wealth manager row in the admin "Veli Migration → Wealth Managers" table: identity, the derived
// lifecycle status, the admin-set risk rating (nullable = unset), and a single client count over the WM's
// book. The per-client activation split lives in the separate activation funnel, not here.
export interface AdminWealthManagerDto {
  id: string
  veliAdvisorId: string
  firstName?: string | null
  lastName?: string | null
  email: string
  phone: string
  status: WealthManagerLifecycleStatus
  agreementSignedAt?: string | null
  riskRating?: RiskTier | null
  clientCount: number
  // Whether the Auth0 login has been minted. The derived status collapses "Active without an invite" to
  // Active, so this exposes the underlying invite state — the screen flags an Active-but-never-Invited WM
  // as a gap (handoff decision 5).
  isInvited: boolean
}

export interface GetWealthManagersResult {
  wealthManagers: AdminWealthManagerDto[]
}

// Set (or clear, with a null rating) a WM's admin-attested risk rating.
export interface SetWealthManagerRiskRatingCommand {
  riskRating?: RiskTier | null
}

// Result of the atomic set-rating-and-rescore-the-book commit: the new rating plus how many of the WM's
// already-scored clients were re-scored under it (ADR-0007 amendment).
export interface SetWealthManagerRiskRatingResponse {
  wealthManagerId: string
  riskRating?: RiskTier | null
  rescoredClients: number
}

export const coreApi = createApi({
  reducerPath: 'coreApi',
  baseQuery,
  tagTypes: [
    'User',
    'Users',
    'Wallets',
    'Swaps',
    'Members',
    'BankAccounts',
    'Currencies',
    'Networks',
    'KycDocuments',
    'KycQuestionnaire',
    'UBOS',
    'OfframpDetails',
    'BrokerPerformance',
    'Performance',
    'FundPerformance',
    'InvestorFundPerformance',
    'FundDeposits',
    'OkxAccounts',
    'AffiliatePartners',
    'CustomerInsights',
    'FeeStructures',
    'FinanstilsynetReports',
    'ComplianceInvestor',
    'RescoreRun',
    'WealthManagers',
    'WealthManagerBook',
    'Team',
  ],
  endpoints: builder => ({
    createAccount: builder.mutation<UserDto, UserCreateCommand>({
      query: body => ({
        url: '/users',
        method: 'POST',
        body,
      }),
    }),
    login: builder.mutation<
      {
        id: string
        jwt: string
      },
      { username: string; password: string }
    >({
      query: body => ({
        url: '/login',
        method: 'POST',
        body,
      }),
    }),
    getUser: builder.query<UserDto, string>({
      query: userId => `/users/${userId}`,
      providesTags: ['User'],
    }),
    startImpersonation: builder.mutation<
      StartImpersonationResponse,
      StartImpersonationRequest
    >({
      query: body => ({
        url: '/admin/impersonation/start',
        method: 'POST',
        body,
      }),
    }),
    stopImpersonation: builder.mutation<void, void>({
      query: () => ({
        url: '/admin/impersonation/stop',
        method: 'POST',
      }),
    }),
    updateUser: builder.mutation<
      void,
      UpdateUserCommandModel & { userId: string }
    >({
      query: body => ({
        url: `/users/${body.userId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['User', 'Users'],
    }),
    createSwap: builder.mutation<
      SwapDto,
      { userId: string; body: SwapCreateCommandModel }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/swaps`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Swaps'],
    }),
    createUserFundDeposit: builder.mutation<
      DepositDto,
      { userId: string; body: CreateUserFundDepositCommandModel }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/fund-deposits`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FundDeposits'],
    }),
    createThorchainSwap: builder.mutation<
      ThorchainSwapDto,
      { userId: string; body: ThorchainSwapCreateCommandModel }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/thorchain-swaps`,
        method: 'POST',
        body,
      }),
    }),
    getSwaps: builder.query<SwapDto[], null>({
      query: () => `/swaps`,
      providesTags: ['Swaps'],
    }),
    getSwap: builder.query<SwapDto, string>({
      query: swapId => `/swaps/${swapId}`,
      providesTags: (result, error, swapId) => [{ type: 'Swaps', id: swapId }],
    }),
    getUsers: builder.query<UserDto[], null>({
      query: () => `/users`,
      providesTags: ['Users'],
    }),
    createSystemWallet: builder.mutation<WalletDto, WalletCreateCommand>({
      query: body => ({
        url: '/wallets',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Wallets'],
    }),
    createWallet: builder.mutation<
      WalletDto,
      { userId: string; body: WalletCreateCommand }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/wallets`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Wallets'],
    }),
    retryWalletScreening: builder.mutation<
      WalletDto,
      { userId: string; walletId: string }
    >({
      query: ({ userId, walletId }) => ({
        url: `/users/${userId}/wallets/${walletId}/retry-screening`,
        method: 'POST',
      }),
      invalidatesTags: ['Wallets'],
    }),
    getWalletScreeningAccessToken: builder.query<
      SumsubAccessTokenDto,
      { userId: string; walletId: string }
    >({
      query: ({ userId, walletId }) => ({
        url: `/users/${userId}/wallets/${walletId}/screening-access-token`,
      }),
    }),
    getExchangeRatePair: builder.query<
      ExchangeRatePairDto,
      { currencyFromIsoCode: string; currencyToIsoCode: string }
    >({
      query: ({ currencyFromIsoCode, currencyToIsoCode }) => ({
        url: `/exchange-rates/${currencyFromIsoCode}/${currencyToIsoCode}`,
      }),
    }),
    getExchangeRates: builder.query<
      ExchangeRateAllDto,
      { currencyIsoCode: string }
    >({
      query: ({ currencyIsoCode }) => ({
        url: `/exchange-rates/${currencyIsoCode}`,
      }),
    }),
    getWallets: builder.query<WalletDto[], null>({
      query: () => `/wallets`,
      providesTags: ['Wallets'],
    }),
    setSwapStatus: builder.mutation<SwapDto, SwapUpdateStatusCommand>({
      query: body => ({
        url: '/swaps/set-swap-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Swaps'],
    }),
    resendWalletVerification: builder.mutation<SwapDto, { swapId: string }>({
      query: ({ swapId }) => ({
        url: `/swaps/${swapId}/resend-wallet-verification`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { swapId }) => [
        'Swaps',
        { type: 'Swaps', id: swapId },
      ],
    }),
    getCurrencies: builder.query<CurrencyDto[], void>({
      query: () => ({
        url: '/currencies',
      }),
      providesTags: ['Currencies'],
    }),
    getBrokerPerformance: builder.query<
      BrokerPerformanceDto,
      { from: string; to: string; currencyId: string }
    >({
      query: params => ({
        url: `/broker/performance?from=${params.from}&to=${params.to}&currencyId=${params.currencyId}`,
      }),
      providesTags: ['BrokerPerformance'],
    }),
    getCustomerInsights: builder.query<
      CustomerInsightsDto,
      CustomerInsightsFilterDto
    >({
      query: params => {
        const searchParams = new URLSearchParams()
        if (params.fromDate) searchParams.append('FromDate', params.fromDate)
        if (params.toDate) searchParams.append('ToDate', params.toDate)
        if (params.customerRole)
          searchParams.append('CustomerRole', params.customerRole)
        if (params.country) searchParams.append('Country', params.country)
        if (params.productId) searchParams.append('ProductId', params.productId)
        if (params.tradeType) searchParams.append('TradeType', params.tradeType)

        return {
          url: `/customer-insights${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        }
      },
      providesTags: ['CustomerInsights'],
    }),
    getFinanstilsynetSamplingList: builder.query<
      FinanstilsynetSamplingListRowDto[],
      FinanstilsynetFilters | void
    >({
      query: params => {
        const searchParams = new URLSearchParams()
        if (params?.from) searchParams.append('From', params.from)
        if (params?.to) searchParams.append('To', params.to)
        const qs = searchParams.toString()
        return {
          url: `/compliance/finanstilsynet/sampling-list${qs ? `?${qs}` : ''}`,
        }
      },
      providesTags: ['FinanstilsynetReports'],
    }),
    getFinanstilsynetCustomerBreakdown: builder.query<
      FinanstilsynetCustomerBreakdownRowDto[],
      FinanstilsynetFilters | void
    >({
      query: params => {
        const searchParams = new URLSearchParams()
        if (params?.from) searchParams.append('From', params.from)
        if (params?.to) searchParams.append('To', params.to)
        const qs = searchParams.toString()
        return {
          url: `/compliance/finanstilsynet/customer-breakdown${qs ? `?${qs}` : ''}`,
        }
      },
      providesTags: ['FinanstilsynetReports'],
    }),
    getNetworks: builder.query<NetworkDto[], void>({
      query: () => ({
        url: '/networks',
      }),
      providesTags: ['Networks'],
    }),
    resendVerificationEmail: builder.mutation<
      Auth0VerificationEmailResponse,
      null
    >({
      query: () => ({
        url: '/users/verification-email',
        method: 'POST',
        body: {},
      }),
    }),
    inviteMember: builder.mutation<Auth0InviteMemberResponse, InviteCommand>({
      query: body => ({
        url: '/members',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Members', 'User'],
    }),
    getQuotation: builder.query<
      TransferQuotationDto,
      {
        amountIn?: number
        amountOut?: number
        currencyInId: string
        currencyOutId: string
      }
    >({
      query: body => {
        let amountParams = ''
        if (body.amountIn) {
          amountParams = `amountIn=${body.amountIn}`
        } else {
          amountParams = `amountOut=${body.amountOut}`
        }

        return {
          url: `/swaps/quotation?${amountParams}&currencyInId=${body.currencyInId}&currencyOutId=${body.currencyOutId}`,
        }
      },
    }),
    acceptClientAgreement: builder.mutation<UserDto, null>({
      query: () => ({
        url: '/users/accept-client-agreement',
        method: 'POST',
      }),
    }),
    // KYC-step state for a Veli-origin user (ADR-0004 §9), read on KYC-step load so
    // the screen renders outcome-tailored copy *before* the client acts. The server
    // derives Veli-origin from the linked import row, so no body is needed. Modelled
    // as a query so it auto-runs on mount, dedupes concurrent calls, and exposes
    // isFetching for the loading state. A plain read since reusable KYC was retired
    // (no partner calls); still a POST, which the API keeps accepting. The screen
    // branches copy off `outcome` — no dependence on a User-cache invalidation
    // landing in time.
    getVeliKycStepState: builder.query<VeliKycStepStateDto, void>({
      query: () => ({
        url: '/kyc/veli-step-state',
        method: 'POST',
        body: {},
      }),
    }),
    // A migrated corporate client supplies the VAT number their Veli record never carried, so their
    // account can be corrected from an Individual to an Organization. Parked on the import row — the only
    // place it can live while the account is still an Individual, since VatNumber is an Organization
    // field. Invalidates 'User' so `requiresOrganizationVatNumber` refetches and the prompt clears itself.
    setVeliOrganizationVatNumber: builder.mutation<void, { vatNumber: string }>({
      query: body => ({
        url: '/veli-migration/organization-vat',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    acceptDisclaimers: builder.mutation<UserDto, void>({
      query: () => ({
        url: '/users/accept-disclaimers',
        method: 'POST',
      }),
    }),
    acceptPortfolioManagementAgreement: builder.mutation<UserDto, null>({
      query: () => ({
        url: '/users/accept-portfolio-management-agreement',
        method: 'POST',
      }),
    }),
    submitComplianceOnboardingAnswers: builder.mutation<
      ComplianceOnboardingStateDto,
      ComplianceOnboardingAnswersDto
    >({
      query: body => ({
        url: '/compliance/onboarding/answers',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    getComplianceOnboardingState: builder.query<
      ComplianceOnboardingStateDto,
      void
    >({
      query: () => ({
        url: '/compliance/onboarding/state',
      }),
      providesTags: ['User'],
    }),
    // Product-activation amend (ADR-0005): declare a not-yet-declared product and
    // answer its Q4 branch; the server merges, re-scores as CustomerUpdate, and
    // returns the fresh onboarding state (products included).
    addProductAnswers: builder.mutation<
      ComplianceOnboardingStateDto,
      AddProductAnswersDto
    >({
      query: body => ({
        url: '/compliance/onboarding/products',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    getComplianceInvestor: builder.query<ComplianceInvestorDto, string>({
      query: investorId => ({
        url: `/compliance/admin/investors/${investorId}`,
      }),
      providesTags: (_result, _error, investorId) => [
        { type: 'ComplianceInvestor', id: investorId },
      ],
    }),
    rescoreComplianceInvestor: builder.mutation<RescoreResultDto, string>({
      query: investorId => ({
        url: `/compliance/admin/rescore/${investorId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, investorId) => [
        { type: 'ComplianceInvestor', id: investorId },
        'Users',
        'User',
      ],
    }),
    updateComplianceInvestorAnswers: builder.mutation<
      UpdateInvestorAnswersResultDto,
      { investorId: string; body: ComplianceOnboardingAnswersDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/investors/${investorId}/answers`,
        method: 'PUT',
        body,
      }),
      // Editing answers always re-scores — invalidate the investor view plus the
      // user caches that may reflect the new state/tier.
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
        'Users',
      ],
    }),
    resyncComplianceInvestor: builder.mutation<ResyncResultDto, string>({
      query: investorId => ({
        url: `/compliance/admin/resync/${investorId}`,
        method: 'POST',
      }),
      // No invalidations — resync only re-emits to CRM, no local state changes.
    }),
    // Starts a background bulk-rescore run (202) — the run outlives any single
    // request, so the result is NOT in this response. Poll getLatestRescoreRun
    // until the run leaves Running; the broad user caches are invalidated on
    // completion (see RuleSetsScreen), not here where nothing has changed yet.
    rescoreStaleComplianceInvestors: builder.mutation<
      RescoreRunStatusDto,
      void
    >({
      query: () => ({
        url: '/compliance/admin/rescore-stale',
        method: 'POST',
      }),
      invalidatesTags: ['RescoreRun'],
    }),
    // Non-destructive dry-run of the bulk rescore: reports what would change
    // without writing anything, so no cache invalidation.
    previewRescoreStaleComplianceInvestors: builder.mutation<
      RescorePreviewDto,
      void
    >({
      query: () => ({
        url: '/compliance/admin/rescore-stale/preview',
        method: 'POST',
      }),
    }),
    // Version-agnostic bulk rescore: re-scores every customer with answers
    // regardless of rule-set version. Use after a fix that changes scoring for
    // customers already on the active version (which rescore-stale would skip).
    // Same 202-and-poll contract as rescoreStaleComplianceInvestors.
    rescoreAllComplianceInvestors: builder.mutation<RescoreRunStatusDto, void>({
      query: () => ({
        url: '/compliance/admin/rescore-all',
        method: 'POST',
      }),
      invalidatesTags: ['RescoreRun'],
    }),
    // The current or most recent background bulk-rescore run (either scope);
    // { run: null } when none has run since the API instance started. The
    // rule-sets screen polls this while a run is in flight.
    getLatestRescoreRun: builder.query<LatestRescoreRunDto, void>({
      query: () => ({
        url: '/compliance/admin/rescore-run',
      }),
      providesTags: ['RescoreRun'],
    }),
    previewRescoreAllComplianceInvestors: builder.mutation<
      RescorePreviewDto,
      void
    >({
      query: () => ({
        url: '/compliance/admin/rescore-all/preview',
        method: 'POST',
      }),
    }),
    // CRM-backup actions: operator-driven equivalents of the seven inbound
    // CRM webhook event types. Each one re-runs the existing inbound handler
    // path via BackupActionInvoker, so domain effects match a live CRM call.
    backupCaseResolve: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupCaseResolveCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/case-resolve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
      ],
    }),
    backupRestrictionDecision: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupRestrictionDecisionCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/restriction-decision`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
      ],
    }),
    backupTierChange: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupTierChangeCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/tier-change`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
      ],
    }),
    backupReviewRequired: builder.mutation<BackupActionResultDto, string>({
      query: investorId => ({
        url: `/compliance/admin/${investorId}/backup/review-required`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, investorId) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
      ],
    }),
    backupSofRequestClose: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupSofRequestCloseCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/sof-request-close`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
      ],
    }),
    backupCustomerExit: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupCustomerExitCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/customer-exit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
        'Users',
      ],
    }),
    backupStrFile: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupStrFiledCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/str-file`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
      ],
    }),
    // Confirm (or un-confirm) a screened customer as a PEP. Sets the sticky
    // User.PepConfirmed flag that forces onboarding EDD regardless of the Q10
    // self-declaration, and re-scores an already-onboarded customer — so it can
    // change tier/restrictions/state and the client lists.
    backupPepConfirm: builder.mutation<
      BackupActionResultDto,
      { investorId: string; body: BackupPepConfirmCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/${investorId}/backup/pep-confirm`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
        'Users',
      ],
    }),
    resetComplianceInvestor: builder.mutation<
      ResetInvestorComplianceResultDto,
      { investorId: string; body: ResetInvestorComplianceCommandDto }
    >({
      query: ({ investorId, body }) => ({
        url: `/compliance/admin/reset/${investorId}`,
        method: 'POST',
        body,
      }),
      // Reset wipes live state — invalidate everything that may reflect it.
      invalidatesTags: (_r, _e, { investorId }) => [
        { type: 'ComplianceInvestor', id: investorId },
        'User',
        'Users',
      ],
    }),
    getSwapActivities: builder.query<SwapActivityDto[], string>({
      query: swapId => ({
        url: `/swaps/${swapId}/activities`,
      }),
    }),
    updateKycStatus: builder.mutation<UserDto, UpdateKycStatusCommand>({
      query: body => ({
        url: `/users/${body.userId}/kyc`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users', 'User'],
    }),
    deleteWallet: builder.mutation<WalletDto, string>({
      query: walletId => ({
        url: `/wallets/${walletId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Wallets'],
    }),
    getBankAccounts: builder.query<BankAccountDto[], null>({
      query: () => `/bank-accounts`,
      providesTags: ['BankAccounts'],
    }),
    createSystemBankAccount: builder.mutation<
      BankAccountDto,
      BankAccountCreateCommand
    >({
      query: body => ({
        url: '/bank-accounts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BankAccounts'],
    }),
    createBankAccount: builder.mutation<
      BankAccountDto,
      { userId: string; body: BankAccountCreateCommand }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/bank-accounts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BankAccounts'],
    }),
    updateBankAccount: builder.mutation<
      BankAccountDto,
      BankAccountUpdateCommand
    >({
      query: body => ({
        url: `/bank-accounts/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['BankAccounts'],
    }),
    deleteBankAccount: builder.mutation<BankAccountDto, string>({
      query: bankAccountId => ({
        url: `/bank-accounts/${bankAccountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BankAccounts'],
    }),
    getAllBankAccountsByUserId: builder.query<
      BankAccountDto[],
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/users/${userId}/bank-accounts`,
        method: 'GET',
      }),
      providesTags: ['BankAccounts'],
    }),
    getAllWallets: builder.query<WalletDto[], void>({
      query: () => ({
        url: '/wallets',
        method: 'GET',
      }),
      providesTags: ['Wallets'],
    }),
    getAllWalletsByUserId: builder.query<WalletDto[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/wallets`,
        method: 'GET',
      }),
      providesTags: ['Wallets'],
    }),
    getAllMembersByOrganizationId: builder.query<
      UserDto[],
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `/organizations/${organizationId}/members`,
        method: 'GET',
      }),
      providesTags: ['Members'],
    }),
    getAllTransfers: builder.query<TransferDto[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/transfers`,
        method: 'GET',
      }),
      providesTags: [],
    }),
    getAllTransferActivity: builder.query<
      TransferActivityDto[],
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/users/${userId}/transfer-activities`,
        method: 'GET',
      }),
      providesTags: [],
    }),
    getAllSwapsById: builder.query<SwapDto[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/swaps`,
        method: 'GET',
      }),
      providesTags: ['Swaps'],
    }),
    getUserFundDeposits: builder.query<DepositDto[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/fund-deposits`,
        method: 'GET',
      }),
      providesTags: ['FundDeposits'],
    }),
    getUserFundDeposit: builder.query<
      DepositDto,
      { userId: string; depositId: string }
    >({
      query: ({ userId, depositId }) => ({
        url: `/users/${userId}/fund-deposits/${depositId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { depositId }) => [
        'FundDeposits',
        { type: 'FundDeposits', id: depositId },
      ],
    }),
    getFeeStructure: builder.query<FeeStructureDto, void>({
      query: () => ({
        url: `/users/fee-structure`,
        method: 'GET',
      }),
      // providesTags: [''],
    }),
    getFeeStructures: builder.query<FeeStructureDto[], void>({
      query: () => ({
        url: `/fee-structures`,
        method: 'GET',
      }),
      providesTags: ['FeeStructures'],
    }),
    getFeeStructureById: builder.query<FeeStructureDto, string>({
      query: id => ({
        url: `/fee-structures/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'FeeStructures', id }],
    }),
    createFeeStructure: builder.mutation<
      FeeStructureDto,
      FeeStructureCreateCommand
    >({
      query: body => ({
        url: `/fee-structures`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FeeStructures'],
    }),
    updateFeeStructure: builder.mutation<
      FeeStructureDto,
      FeeStructureCreateCommand & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/fee-structures/${id}`,
        method: 'PUT',
        body: { id, ...body },
      }),
      invalidatesTags: (result, error, { id }) => [
        'FeeStructures',
        { type: 'FeeStructures', id },
      ],
    }),
    deleteFeeStructure: builder.mutation<FeeStructureDto, string>({
      query: id => ({
        url: `/fee-structures/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        'FeeStructures',
        { type: 'FeeStructures', id },
      ],
    }),
    getFeeStructureTestQuotation: builder.query<
      TransferQuotationDto,
      {
        feeStructureId: string
        amountIn?: number
        amountOut?: number
        currencyInId: string
        currencyOutId: string
      }
    >({
      query: ({
        feeStructureId,
        amountIn,
        amountOut,
        currencyInId,
        currencyOutId,
      }) => {
        const params = new URLSearchParams()
        if (amountIn !== undefined) {
          params.append('amountIn', amountIn.toString())
        }
        if (amountOut !== undefined) {
          params.append('amountOut', amountOut.toString())
        }
        params.append('currencyInId', currencyInId)
        params.append('currencyOutId', currencyOutId)

        return {
          url: `/fee-structures/${feeStructureId}/test-quotation?${params.toString()}`,
          method: 'POST',
        }
      },
    }),
    getUserFeeStructure: builder.query<FeeStructureDto, string>({
      query: userId => ({
        url: `/users/${userId}/fee-structure`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [
        { type: 'User', id: userId },
        'FeeStructures',
      ],
    }),
    assignUserFeeStructure: builder.mutation<
      void,
      { userId: string; feeStructureId: string }
    >({
      query: ({ userId, feeStructureId }) => ({
        url: `/users/${userId}/fee-structure`,
        method: 'PUT',
        body: { feeStructureId },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
        'FeeStructures',
      ],
    }),
    getOfframpDetails: builder.query<CurrencyToOffRampDto[], null>({
      query: () => ({
        url: `/currencies/offramp-details`,
        method: 'GET',
      }),
      providesTags: ['OfframpDetails'],
    }),
    updateCurrency: builder.mutation<
      CurrencyDto,
      { id: string; body: UpdateCurrencyCommandModel }
    >({
      query: ({ id, body }) => ({
        url: `/currencies/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Currencies', 'OfframpDetails'],
    }),
    updateSwap: builder.mutation<SwapDto, SwapUpdateCommand>({
      query: body => ({
        url: `/swaps`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Swaps'],
    }),
    toggleFavourite: builder.mutation<
      {},
      { currencyId: string; isFavourite: boolean }
    >({
      query: body => ({
        url: `/currencies/favourite`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    createNetwork: builder.mutation<NetworkDto, NetworkCreateCommand>({
      query: body => ({
        url: `/networks`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Networks', 'OfframpDetails'],
    }),
    updateNetwork: builder.mutation<NetworkDto, NetworkUpdateCommand>({
      query: body => ({
        url: `/networks/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Networks', 'OfframpDetails', 'Currencies'],
    }),
    uploadNetworkIcon: builder.mutation<
      NetworkDto,
      { networkId: string; formData: FormData }
    >({
      query: ({ networkId, formData }) => ({
        url: `/networks/upload/${networkId}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Networks', 'OfframpDetails'],
    }),
    createCurrency: builder.mutation<CurrencyDto, CurrencyCreateCommand>({
      query: body => ({
        url: `/currencies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Currencies', 'OfframpDetails'],
    }),
    uploadCurrencyIcon: builder.mutation<
      CurrencyDto,
      { currencyId: string; formData: FormData }
    >({
      query: ({ currencyId, formData }) => ({
        url: `/currencies/upload/${currencyId}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Currencies', 'OfframpDetails'],
    }),
    otacGetSwapInvoiceDetails: builder.query<SwapInvoiceDto, string>({
      query: id => ({
        url: `/otac/swaps/${id}/invoice-details`,
        method: 'GET',
      }),
    }),
    otacGetFundDepositInvoiceDetails: builder.query<
      FundDepositInvoiceDto,
      string
    >({
      query: id => ({
        url: `/otac/fund-deposits/${id}/invoice-details`,
        method: 'GET',
      }),
    }),
    getExchangeRateRawCmcInEur: builder.query<
      ExchangeRatePairDto,
      { currencyFromIsoCode: string }
    >({
      query: ({ currencyFromIsoCode }) => ({
        url: `/exchange-rates/raw-cmc/${currencyFromIsoCode}`,
      }),
    }),
    suspendAccount: builder.mutation<UserDto, SuspendAccountCommand>({
      query: body => ({
        url: `/users/${body.userId}/suspend`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users', 'User'],
    }),
    // Back-office change of a customer's login email (User.Email + the Auth0 identity). The new address is
    // set unverified and Auth0 sends a verification email, so the customer must re-confirm the new mailbox.
    changeUserEmail: builder.mutation<
      UserDto,
      { userId: string; newEmail: string }
    >({
      query: body => ({
        url: `/users/${body.userId}/email`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users', 'User'],
    }),
    closeAccount: builder.mutation<UserDto, CloseMyAccountCommand>({
      query: body => ({
        url: '/users/close-account',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users', 'User'],
    }),
    //Admin
    getFundPerformance: builder.query<
      FundPerformanceDto[],
      { start: string; end: string } | null
    >({
      query: payload => ({
        url: payload
          ? `/fund/performance?start=${payload.start}&end=${payload.end}`
          : `/fund/performance`,
        method: 'GET',
      }),
      providesTags: ['FundPerformance'],
    }),
    updateFundPerformance: builder.mutation<any, UpdateFundPerformanceCommand>({
      query: body => ({
        url: `/fund/performance`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FundPerformance'],
    }),
    createFundDeposit: builder.mutation<any, CreateFundDepositCommand>({
      query: body => ({
        url: `/fund/deposits`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FundDeposits'],
    }),
    getAllAdminFundDeposits: builder.query<AdminFundDepositDto[], void>({
      query: () => ({
        url: `/fund/deposits`,
        method: 'GET',
      }),
      providesTags: ['FundDeposits'],
    }),
    updateFundDeposit: builder.mutation<
      any,
      UpdateFundDepositCommandModel & { depositId: string }
    >({
      query: ({ depositId, amount, transferStatus, transferredAt }) => ({
        url: `/fund/deposits/${depositId}`,
        method: 'PATCH',
        body: {
          amount,
          transferStatus,
          transferredAt,
        },
      }),
      invalidatesTags: ['FundDeposits'],
    }),
    //Investor
    getInvestorFundPerformance: builder.query<
      InvestorFundPerformanceDto[],
      { start: string; end: string; convertToEur: boolean }
    >({
      query: ({ start, end, convertToEur }) => ({
        url: `/users/fund-performance?start=${start}&end=${end}&convertToEur=${convertToEur}`,
        method: 'GET',
      }),
      providesTags: ['InvestorFundPerformance'],
    }),
    getFundDeposits: builder.query<DepositDto[], { investorId: string }>({
      query: ({ investorId }) => ({
        url: `/fund/deposits/${investorId}`,
        method: 'GET',
      }),
      providesTags: ['FundDeposits'],
    }),
    deleteFundDeposit: builder.mutation<DepositDto, string>({
      query: fundDepositId => ({
        url: `/fund/deposits/${fundDepositId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FundDeposits'],
    }),
    mfaEnroll: builder.mutation<MfaEnrollDto, void>({
      query: () => ({
        url: '/mfa/enroll',
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    mfaVerify: builder.mutation<MfaVerifyDto, MfaVerifyCommand>({
      query: body => ({
        url: '/mfa/verify',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    mfaDisable: builder.mutation<void, MfaDisableCommand>({
      query: body => ({
        url: '/mfa/disable',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    mfaChallenge: builder.mutation<MfaChallengeDto, MfaChallengeCommand>({
      query: body => ({
        url: '/mfa/challenge',
        method: 'POST',
        body,
        credentials: 'include',
      }),
    }),
    mfaRemember: builder.query<
      MfaChallengeDto,
      { sessionToken: string; state: string }
    >({
      query: params => ({
        url: `/mfa/remember?sessionToken=${params.sessionToken}&state=${params.state}`,
        method: 'GET',
        credentials: 'include',
      }),
    }),
    getKycAccessToken: builder.query<SumsubAccessTokenDto, void>({
      query: () => ({
        url: '/kyc/access-token',
        method: 'GET',
      }),
    }),
    // The pre-KYC country gate: declare the country of residence, checked against the Country Risk Register
    // before any Sumsub applicant is created. A serviceable residence is committed on the user and returns
    // eligible (it routes the Sumsub level server-side), so invalidate User to pull the committed
    // declaredResidenceIso — the KYC screens gate on it. A Restricted/Rejected residence returns not-eligible
    // and commits nothing (a soft block).
    declareKycResidence: builder.mutation<
      KycResidenceEligibilityDto,
      DeclareKycResidenceCommand
    >({
      query: body => ({
        url: '/kyc/residence',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
      // A User read already in flight when the residence commits (the Get Started poll) lands the pre-commit
      // user, and RTK Query 1.x drops the invalidation refetch for a query that is still pending ("Don't retry
      // a request that's currently in-flight"). The stale user would then reopen the gate on the KYC screen
      // with nothing left to close it, so refetch once those reads have settled. They are waited out on the
      // cache state: getRunningQueriesThunk() only tracks initiate() calls, and a poll or invalidation refetch
      // is not one.
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (!data.eligible) return
          const isUserReadPending = () => {
            const state = getState()
            return coreApi.util
              .selectInvalidatedBy(state, ['User'])
              .some(
                ({ queryCacheKey }) =>
                  state.coreApi.queries[queryCacheKey]?.status ===
                  QueryStatus.pending
              )
          }
          while (isUserReadPending())
            await new Promise(resolve => setTimeout(resolve, 50))
          dispatch(coreApi.util.invalidateTags(['User']))
        } catch {
          // A failed declaration reaches the caller through unwrap(); there is nothing to refetch.
        }
      },
    }),
    updateWallet: builder.mutation<WalletDto, WalletUpdateCommand>({
      query: body => ({
        url: `/wallets/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Wallets'],
    }),
    getInvestorOkxPerformance: builder.query<
      any,
      { investorId: string; currency: string }
    >({
      query: ({ investorId, currency }) => ({
        url: `/users/${investorId}/okx-performance?currency=${currency}`,
        method: 'GET',
      }),
    }),
    // New multi-account OKX endpoints
    getOkxAccounts: builder.query<OkxAccountDto[], { investorId: string }>({
      query: ({ investorId }) => ({
        url: `/users/${investorId}/okx-accounts`,
        method: 'GET',
      }),
      providesTags: ['OkxAccounts'],
    }),
    createOkxAccount: builder.mutation<
      OkxAccountDto,
      { investorId: string; account: CreateOkxAccountDto }
    >({
      query: ({ investorId, account }) => ({
        url: `/users/${investorId}/okx-accounts`,
        method: 'POST',
        body: account,
      }),
      invalidatesTags: ['OkxAccounts', 'User', 'Users'],
    }),
    updateOkxAccount: builder.mutation<
      OkxAccountDto,
      { investorId: string; okxAccountId: string; account: UpdateOkxAccountDto }
    >({
      query: ({ investorId, okxAccountId, account }) => ({
        url: `/users/${investorId}/okx-accounts/${okxAccountId}`,
        method: 'PUT',
        body: account,
      }),
      invalidatesTags: ['OkxAccounts'],
    }),
    deleteOkxAccount: builder.mutation<
      void,
      { investorId: string; okxAccountId: string }
    >({
      query: ({ investorId, okxAccountId }) => ({
        url: `/users/${investorId}/okx-accounts/${okxAccountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['OkxAccounts', 'User', 'Users'],
    }),
    provisionOkxSubAccount: builder.mutation<
      ProvisionOkxSubAccountResponse,
      { investorId: string; investmentPackage: string }
    >({
      query: ({ investorId, investmentPackage }) => ({
        url: `/users/${investorId}/okx-accounts/provision`,
        method: 'POST',
        body: { investmentPackage },
      }),
      invalidatesTags: ['OkxAccounts', 'User', 'Users'],
    }),
    collectOkxBalanceData: builder.mutation<
      CollectOkxBalanceDataResponse,
      { investorId?: string }
    >({
      query: ({ investorId }) => ({
        url: `/users/collect-okx-balance-data${investorId ? `?investorId=${investorId}` : ''}`,
        method: 'POST',
      }),
    }),
    getOkxBalanceHistory: builder.query<
      OkxBalanceHistoryDto[],
      {
        investorId: string
        startDate?: string
        endDate?: string
        currency?: string
        okxAccountId?: string
      }
    >({
      query: ({ investorId, startDate, endDate, currency, okxAccountId }) => {
        const params = new URLSearchParams()
        if (startDate) params.append('startDate', startDate)
        if (endDate) params.append('endDate', endDate)
        if (currency) params.append('currency', currency)
        if (okxAccountId) params.append('okxAccountId', okxAccountId)

        return {
          url: `/users/${investorId}/okx-balance-history${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        }
      },
    }),
    getOkxPortfolioMetrics: builder.query<
      OkxPortfolioMetricsDto,
      {
        investorId: string
        okxAccountId?: string
        startDate?: string
        endDate?: string
        granularity?: string
        currency?: string
      }
    >({
      query: ({
        investorId,
        okxAccountId,
        startDate,
        endDate,
        granularity,
        currency,
      }) => {
        const params = new URLSearchParams()
        if (okxAccountId) params.append('okxAccountId', okxAccountId)
        if (startDate) params.append('startDate', startDate)
        if (endDate) params.append('endDate', endDate)
        if (granularity) params.append('granularity', granularity)
        if (currency) params.append('currency', currency)

        return {
          url: `/users/${investorId}/okx-portfolio-metrics${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        }
      },
      providesTags: ['OkxAccounts'],
    }),
    getAllLegacySwaps: builder.query<SwapInvoiceDto[], void>({
      query: () => ({
        url: '/swaps/legacy',
        method: 'GET',
      }),
    }),
    otacGetLegacySwapInvoiceDetails: builder.query<SwapInvoiceDto, string>({
      query: id => ({
        url: `/otac/legacy-swaps/${id}/invoice-details`,
        method: 'GET',
      }),
    }),
    // Team (internal staff) endpoints
    getTeamMembers: builder.query<TeamMemberDto[], void>({
      query: () => ({
        url: '/team',
        method: 'GET',
      }),
      providesTags: ['Team'],
    }),
    inviteStaffMember: builder.mutation<
      InviteStaffMemberResponse,
      InviteStaffMemberCommand
    >({
      query: body => ({
        url: '/team/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team'],
    }),
    // Affiliate Partner endpoints
    createAffiliatePartner: builder.mutation<
      AffiliatePartnerDto,
      AffiliatePartnerCreateCommand
    >({
      query: body => ({
        url: '/affiliate-partners',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AffiliatePartners'],
    }),
    getAllAffiliatePartners: builder.query<AffiliatePartnerDto[], void>({
      query: () => ({
        url: '/affiliate-partners',
        method: 'GET',
      }),
      providesTags: ['AffiliatePartners'],
    }),
    getAffiliatePartner: builder.query<AffiliatePartnerDto, string>({
      query: id => ({
        url: `/affiliate-partners/${id}`,
        method: 'GET',
      }),
      providesTags: ['AffiliatePartners'],
    }),
    updateAffiliatePartner: builder.mutation<
      AffiliatePartnerDto,
      UpdateAffiliatePartnerCommandModel & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/affiliate-partners/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AffiliatePartners'],
    }),
    deleteAffiliatePartner: builder.mutation<void, string>({
      query: id => ({
        url: `/affiliate-partners/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AffiliatePartners'],
    }),
    getAffiliateStats: builder.query<
      AffiliateStatsDto,
      {
        id: string
        fromDate?: string
        toDate?: string
      }
    >({
      query: ({ id, fromDate, toDate }) => {
        const params = new URLSearchParams()
        if (fromDate) params.append('fromDate', fromDate)
        if (toDate) params.append('toDate', toDate)

        return {
          url: `/affiliate-partners/${id}/stats${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        }
      },
    }),
    getAffiliatePublicStats: builder.query<
      AffiliateStatsDto,
      {
        apiKey: string
        fromDate?: string
        toDate?: string
      }
    >({
      query: ({ apiKey, fromDate, toDate }) => {
        const params = new URLSearchParams()
        if (fromDate) params.append('fromDate', fromDate)
        if (toDate) params.append('toDate', toDate)

        return {
          url: `/affiliate-partners/public-stats${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          headers: {
            'X-Affiliate-API-Key': apiKey,
          },
        }
      },
    }),
    getKrakenClosedOrders: builder.query<
      KrakenClosedOrdersDto,
      {
        start?: string
        end?: string
      }
    >({
      query: ({ start, end }) => {
        const params = new URLSearchParams()
        if (start) params.append('start', start)
        if (end) params.append('end', end)

        return {
          url: `/kraken/closed-orders${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        }
      },
    }),
    // The calling wealth manager's own book of clients with their migration status (issue #83,
    // ADR-0006). Scoped server-side to the caller's id and gated on the WM being active.
    getWealthManagerBook: builder.query<WealthManagerBookDto, void>({
      query: () => ({
        url: '/veli-migration/wealth-managers/me/book',
        method: 'GET',
      }),
      providesTags: ['WealthManagerBook'],
    }),
    // Nudge a single not-yet-activated client in the caller's own book (issue #85, ADR-0006 §5). The
    // activation link is emailed to the client's mailbox; own-book + not-activated guards are enforced
    // server-side. There is no throttle — the button disables transiently on the client after a send.
    remindWealthManagerClient: builder.mutation<
      RemindWealthManagerClientResult,
      string
    >({
      query: veliUserId => ({
        url: `/veli-migration/wealth-managers/me/clients/${encodeURIComponent(veliUserId)}/reminder`,
        method: 'POST',
      }),
    }),
    // Remove a client from the caller's own book when the WM ends the cooperation (ADR-0016). Any
    // migration status is eligible; own-book guard enforced server-side. The client keeps their Penning
    // account as a direct client, so the book is refetched to drop the row.
    unlinkWealthManagerClient: builder.mutation<
      UnlinkWealthManagerClientResult,
      string
    >({
      query: veliUserId => ({
        url: `/veli-migration/wealth-managers/me/clients/${encodeURIComponent(veliUserId)}/unlink`,
        method: 'POST',
      }),
      invalidatesTags: ['WealthManagerBook'],
    }),
    // Self-service resend of a transferring Veli client's activation link to their own mailbox
    // (ADR-0004 §3/§4). Public + unauthenticated: a client turned away from normal signup
    // (code `VeliClientMustActivate`) recovers their link by entering their email. Deliberately
    // non-committal — always succeeds regardless of whether the email matched a seeded row — so it
    // reveals nothing about which addresses are Veli clients; the link only ever reaches the mailbox,
    // never the response.
    resendVeliActivationLink: builder.mutation<void, string>({
      query: email => ({
        url: '/veli-migration/activation/resend',
        method: 'POST',
        body: { email },
      }),
    }),
    // Admin: the seeded wealth managers with a derived Seeded/Invited/Active status, the admin-set risk
    // rating, and a per-WM client count (Veli migration admin area). Admin-only server-side.
    getWealthManagers: builder.query<GetWealthManagersResult, void>({
      query: () => ({
        url: '/veli-migration/wealth-managers',
        method: 'GET',
      }),
      providesTags: ['WealthManagers'],
    }),
    // Admin: mint the Auth0 login for a seeded WM and fire the invite email. 400s if already invited or
    // on an email collision — the screen surfaces that as an error toast.
    inviteWealthManager: builder.mutation<InviteWealthManagerResponse, string>({
      query: id => ({
        url: `/veli-migration/wealth-managers/${id}/invite`,
        method: 'POST',
      }),
      invalidatesTags: ['WealthManagers'],
    }),
    // Admin: attest that a WM's out-of-band agreement is signed (grants compliance visibility). Idempotent
    // — keeps the first timestamp, never un-activates. Labelled "Mark agreement signed", never "Activate".
    activateWealthManager: builder.mutation<
      ActivateWealthManagerResponse,
      string
    >({
      query: id => ({
        url: `/veli-migration/wealth-managers/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: ['WealthManagers'],
    }),
    // Admin: set (or clear) a WM's admin-attested risk rating. A WM is a User, not an Investor, so this is
    // attested rather than engine-computed (CONTEXT.md).
    // Preview a WM rating change before committing: scores the WM's book against the proposed rating and
    // reports what would change, writing nothing (ADR-0007 amendment). The modal runs this before the
    // atomic commit so the MLRO sees the tier flips / cases / restriction changes first.
    previewWealthManagerRatingChange: builder.mutation<
      RescorePreviewDto,
      { id: string } & SetWealthManagerRiskRatingCommand
    >({
      query: ({ id, ...body }) => ({
        url: `/veli-migration/wealth-managers/${id}/risk-rating/preview`,
        method: 'POST',
        body,
      }),
    }),
    // Atomically set (or clear) the rating AND re-score the WM's book under it. The rating and the client
    // rescore commit together — touches the WM's clients, so invalidate the broad user caches too.
    setWealthManagerRiskRating: builder.mutation<
      SetWealthManagerRiskRatingResponse,
      { id: string } & SetWealthManagerRiskRatingCommand
    >({
      query: ({ id, ...body }) => ({
        url: `/veli-migration/wealth-managers/${id}/risk-rating`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WealthManagers', 'Users', 'User'],
    }),
    // Admin: the whole-migration activation funnel (all books), binary Seeded/Activated per client with
    // per-WM counts (ADR-0004). Optionally narrowed to a single WM's book. Admin-only server-side.
    getVeliActivationFunnel: builder.query<
      VeliActivationFunnel,
      { wealthManagerId?: string } | void
    >({
      query: arg => {
        const wealthManagerId =
          arg && 'wealthManagerId' in arg ? arg.wealthManagerId : undefined
        return {
          url: '/veli-migration/activation-funnel',
          method: 'GET',
          params: wealthManagerId ? { wealthManagerId } : undefined,
        }
      },
      providesTags: ['WealthManagers'],
    }),
    // Admin: nudge every not-yet-activated client in one WM's book (ADR-0004). Re-sends the bare-id
    // activation link to the clients' mailboxes; already-activated clients are never re-contacted.
    remindVeliBook: builder.mutation<SendVeliRemindersResult, string>({
      query: wealthManagerId => ({
        url: `/veli-migration/reminders/${wealthManagerId}`,
        method: 'POST',
      }),
    }),
    // Contact the support desk. Composes the signed-in user's short message into an email to the shared
    // support mailbox server-side — the replacement for the personal phone number that used to sit behind
    // the "Need help?" button.
    contactSupport: builder.mutation<
      { sent: boolean },
      { subject: string; message: string }
    >({
      query: body => ({
        url: '/support/contact',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetWealthManagerBookQuery,
  useRemindWealthManagerClientMutation,
  useUnlinkWealthManagerClientMutation,
  useResendVeliActivationLinkMutation,
  useGetWealthManagersQuery,
  useInviteWealthManagerMutation,
  useActivateWealthManagerMutation,
  usePreviewWealthManagerRatingChangeMutation,
  useSetWealthManagerRiskRatingMutation,
  useGetVeliActivationFunnelQuery,
  useRemindVeliBookMutation,
  useUpdateFundDepositMutation,
  useDeleteFundDepositMutation,
  useGetFundDepositsQuery,
  useCreateFundDepositMutation,
  useGetAllAdminFundDepositsQuery,
  useCreateUserFundDepositMutation,
  useLazyGetInvestorFundPerformanceQuery,
  useGetInvestorFundPerformanceQuery,
  useUpdateFundPerformanceMutation,
  useGetFundPerformanceQuery,
  useCreateAccountMutation,
  useLoginMutation,
  useCreateSwapMutation,
  useCreateThorchainSwapMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useStartImpersonationMutation,
  useStopImpersonationMutation,
  useGetUsersQuery,
  useCreateWalletMutation,
  useRetryWalletScreeningMutation,
  useGetWalletScreeningAccessTokenQuery,
  useCreateSystemWalletMutation,
  useGetWalletsQuery,
  useGetExchangeRatesQuery,
  useGetSwapsQuery,
  useGetSwapQuery,
  useSetSwapStatusMutation,
  useResendWalletVerificationMutation,
  useGetNetworksQuery,
  useGetCurrenciesQuery,
  useGetBrokerPerformanceQuery,
  useGetCustomerInsightsQuery,
  useLazyGetCustomerInsightsQuery,
  useGetFinanstilsynetSamplingListQuery,
  useLazyGetFinanstilsynetSamplingListQuery,
  useGetFinanstilsynetCustomerBreakdownQuery,
  useLazyGetFinanstilsynetCustomerBreakdownQuery,
  useGetExchangeRatePairQuery,
  useLazyGetExchangeRatePairQuery,
  useResendVerificationEmailMutation,
  useInviteMemberMutation,
  useGetQuotationQuery,
  useAcceptClientAgreementMutation,
  useGetVeliKycStepStateQuery,
  useAcceptDisclaimersMutation,
  useSetVeliOrganizationVatNumberMutation,
  useAcceptPortfolioManagementAgreementMutation,
  useSubmitComplianceOnboardingAnswersMutation,
  useGetComplianceOnboardingStateQuery,
  useDeclareKycResidenceMutation,
  useAddProductAnswersMutation,
  useGetComplianceInvestorQuery,
  useRescoreComplianceInvestorMutation,
  useUpdateComplianceInvestorAnswersMutation,
  useResyncComplianceInvestorMutation,
  useRescoreStaleComplianceInvestorsMutation,
  usePreviewRescoreStaleComplianceInvestorsMutation,
  useRescoreAllComplianceInvestorsMutation,
  usePreviewRescoreAllComplianceInvestorsMutation,
  useGetLatestRescoreRunQuery,
  useBackupCaseResolveMutation,
  useBackupRestrictionDecisionMutation,
  useBackupTierChangeMutation,
  useBackupReviewRequiredMutation,
  useBackupSofRequestCloseMutation,
  useBackupCustomerExitMutation,
  useBackupStrFileMutation,
  useBackupPepConfirmMutation,
  useResetComplianceInvestorMutation,
  useGetSwapActivitiesQuery,
  useUpdateKycStatusMutation,
  useDeleteWalletMutation,
  useGetBankAccountsQuery,
  useCreateBankAccountMutation,
  useCreateSystemBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
  useGetAllBankAccountsByUserIdQuery,
  useLazyGetAllBankAccountsByUserIdQuery,
  useGetAllWalletsByUserIdQuery,
  useLazyGetAllWalletsByUserIdQuery,
  useGetAllMembersByOrganizationIdQuery,
  useLazyGetAllMembersByOrganizationIdQuery,
  useGetAllTransfersQuery,
  useLazyGetAllTransfersQuery,
  useGetAllTransferActivityQuery,
  useLazyGetAllTransferActivityQuery,
  useGetAllSwapsByIdQuery,
  useLazyGetAllSwapsByIdQuery,
  useGetUserFundDepositsQuery,
  useLazyGetUserFundDepositsQuery,
  useGetUserFundDepositQuery,
  useLazyGetUserFundDepositQuery,
  useGetFeeStructureQuery,
  useGetFeeStructuresQuery,
  useLazyGetFeeStructuresQuery,
  useGetFeeStructureByIdQuery,
  useLazyGetFeeStructureByIdQuery,
  useCreateFeeStructureMutation,
  useUpdateFeeStructureMutation,
  useDeleteFeeStructureMutation,
  useGetFeeStructureTestQuotationQuery,
  useGetUserFeeStructureQuery,
  useLazyGetUserFeeStructureQuery,
  useAssignUserFeeStructureMutation,
  useUpdateUserMutation,
  useGetOfframpDetailsQuery,
  useUpdateCurrencyMutation,
  useUpdateSwapMutation,
  useToggleFavouriteMutation,
  useCreateNetworkMutation,
  useUpdateNetworkMutation,
  useUploadNetworkIconMutation,
  useCreateCurrencyMutation,
  useUploadCurrencyIconMutation,
  useOtacGetSwapInvoiceDetailsQuery,
  useOtacGetFundDepositInvoiceDetailsQuery,
  useLazyGetExchangeRateRawCmcInEurQuery,
  useSuspendAccountMutation,
  useChangeUserEmailMutation,
  useCloseAccountMutation,
  useMfaEnrollMutation,
  useMfaVerifyMutation,
  useMfaDisableMutation,
  useMfaChallengeMutation,
  useMfaRememberQuery,
  useGetKycAccessTokenQuery,
  useGetAllWalletsQuery,
  useUpdateWalletMutation,
  useGetInvestorOkxPerformanceQuery,
  useLazyGetInvestorOkxPerformanceQuery,
  useGetOkxAccountsQuery,
  useLazyGetOkxAccountsQuery,
  useCreateOkxAccountMutation,
  useUpdateOkxAccountMutation,
  useDeleteOkxAccountMutation,
  useProvisionOkxSubAccountMutation,
  useCollectOkxBalanceDataMutation,
  useGetOkxBalanceHistoryQuery,
  useLazyGetOkxBalanceHistoryQuery,
  useGetOkxPortfolioMetricsQuery,
  useLazyGetOkxPortfolioMetricsQuery,
  useGetAllLegacySwapsQuery,
  useLazyGetAllLegacySwapsQuery,
  useOtacGetLegacySwapInvoiceDetailsQuery,
  useLazyOtacGetLegacySwapInvoiceDetailsQuery,
  // Team (internal staff) hooks
  useGetTeamMembersQuery,
  useInviteStaffMemberMutation,
  // Affiliate Partner hooks
  useCreateAffiliatePartnerMutation,
  useGetAllAffiliatePartnersQuery,
  useLazyGetAllAffiliatePartnersQuery,
  useGetAffiliatePartnerQuery,
  useLazyGetAffiliatePartnerQuery,
  useUpdateAffiliatePartnerMutation,
  useDeleteAffiliatePartnerMutation,
  useGetAffiliateStatsQuery,
  useLazyGetAffiliateStatsQuery,
  useGetAffiliatePublicStatsQuery,
  useLazyGetAffiliatePublicStatsQuery,
  useGetKrakenClosedOrdersQuery,
  useLazyGetKrakenClosedOrdersQuery,
  useContactSupportMutation,
} = coreApi

// Whether a Veli activation link is still live, or already consumed. Mirrors the
// API's `VeliActivationState`; the API serialises enums as strings. Defined here
// (not in the generated coreApi-types.ts) so `gen:types` can't wipe it before the
// Veli endpoints land in the OpenAPI spool.
export type VeliActivationState = 'ReadyToActivate' | 'AlreadyActivated'

// Whether the activation page renders the individual or company form (issue #88).
export type VeliClientType = 'Individual' | 'Company'

// The only data the unauthenticated activation landing route exposes for a Veli
// id (ADR-0004 §3): no PII beyond a masked email and the wealth manager's name,
// plus the client type (and, for a company, its read-only legal name) so the page
// can pick the right form.
// Outcome of POST /kyc/veli-step-state (GetVeliKycStepState). `Reused` → donor docs
// were imported before reusable KYC was retired; continue in the WebSDK on the Veli
// level to fill the gaps. `FreshKyc`/`NotVeliOrigin` → proceed to the standard
// WebSDK. Serialized as the enum name by the API's JsonStringEnumConverter.
export type VeliKycReuseOutcome = 'Reused' | 'FreshKyc' | 'NotVeliOrigin'

// The KYC-step state the screen branches on (ADR-0004 §9): the reuse `outcome` plus the
// current `kycStatus`, so the response is self-contained.
export interface VeliKycStepStateDto {
  outcome: VeliKycReuseOutcome
  kycStatus: KycStatus
}

export interface VeliActivationLandingDto {
  state: VeliActivationState
  maskedEmail: string
  /** The owning wealth manager's display name, or null for a direct client. */
  wealthManagerName?: string | null
  clientType: VeliClientType
  /** The company's legal name for a company row; null for an individual. */
  companyName?: string | null
  /** An individual's masked name (e.g. "G*** H***"), pre-filled read-only; null for a company. */
  maskedName?: string | null
  /** An individual's masked phone (e.g. "•••••••••84"), pre-filled read-only; null for a company. */
  maskedPhone?: string | null
  /** An individual whose import row carries no name — the page must collect first/last from the client. */
  requiresName: boolean
  /** An individual whose import row carries no phone — the page must collect it from the client. */
  requiresPhone: boolean
  /** A company whose import row carries no VAT number — the page must collect it from the client. */
  requiresVatNumber: boolean
}

// The activation body. The email is always fixed from the import row server-side and never sent from the
// page. An individual's name + phone are fixed from the row where the extract carried them; where it left
// a hole (a Veli user who never finished onboarding), the page collects the field and sends it here — the
// server uses it only to fill the hole, never to overwrite a value the row already holds. A company sends
// its phone, and its VAT number on the same fill-the-hole terms (its legal name is always fixed from the
// row). `confirmedEmail` is an identity check, not a choice: the client types back the email their Veli
// account uses (shown only masked) so a wrong party who reached the link can't activate. The server gates
// on it but always keeps the row's email.
export interface ActivateVeliClientCommand {
  veliUserId: string
  phone?: string
  firstName?: string
  lastName?: string
  vatNumber?: string
  password: string
  confirmedEmail: string
}

// Public API for endpoints that don't require authentication.
// --- Office-TV platform dashboard ------------------------------------------------------------------
// Hand-written here (not in generated coreApi-types.ts) so `gen:types` can't wipe them before the
// `/dashboard/platform-stats` endpoint lands in the OpenAPI spool. Mirrors the API's
// PlatformDashboardDto: platform-wide aggregates plus an anonymised activity feed (country + amount +
// kind only — never a person). `decimal` serialises as `number`, `DateTime` as an ISO string.
export type DashboardMetricUnit = 'count' | 'money'

export interface DashboardMetric {
  key: string
  label: string
  value: number
  unit: DashboardMetricUnit
  /** Optional "this period" figure shown as "+<secondary> <secondaryLabel>" (same unit as value). */
  secondary?: number | null
  /** The secondary figure a period ago, so the UI can draw a week-over-week trend arrow. */
  secondaryPrevious?: number | null
  /** Label for the secondary figure (e.g. "this week"). */
  secondaryLabel?: string | null
  /** Static caption under the value for single-figure tiles (e.g. "per trade"); no number/trend. */
  caption?: string | null
}

export interface VeliMigrationProgress {
  totalClients: number
  activatedClients: number
}

export interface DashboardDailyPoint {
  date: string
  orderCount: number
  volume: number
}

export interface DashboardTradeTypeSlice {
  key: string
  label: string
  orderCount: number
  volume: number
}

export interface DashboardAssetRow {
  isoCode: string
  name: string
  orderCount: number
  volume: number
  /** Asset icon URL (from its Currency); may be empty, in which case the UI shows a coin glyph. */
  iconUrl: string
}

export interface DashboardCountryRow {
  country: string
  customerCount: number
}

export type DashboardActivityKind =
  | 'swap'
  | 'deposit'
  | 'withdraw'
  | 'new_customer'

export interface DashboardActivity {
  kind: DashboardActivityKind
  occurredAtUtc: string
  country?: string | null
  amount?: number | null
  assetIsoCode?: string | null
}

export interface PlatformDashboardDto {
  generatedAtUtc: string
  /** ISO code (e.g. "DKK", "EUR") every money figure in this snapshot is expressed in. */
  currency: string
  headline: DashboardMetric[]
  veliMigration: VeliMigrationProgress
  volumePerDay: DashboardDailyPoint[]
  tradeTypeSplit: DashboardTradeTypeSlice[]
  topAssets: DashboardAssetRow[]
  topCountries: DashboardCountryRow[]
  recentActivity: DashboardActivity[]
}

export const publicApi = createApi({
  reducerPath: 'publicApi',
  baseQuery: publicBaseQuery,
  endpoints: builder => ({
    // The secret-gated office-TV dashboard snapshot. The screen polls this (pollingInterval is set at
    // the call site); the secret rides in the link's `?secret=` param and is forwarded as a header.
    getPlatformDashboard: builder.query<
      PlatformDashboardDto,
      { secret: string; currency?: string }
    >({
      query: ({ secret, currency }) => ({
        url: '/dashboard/platform-stats',
        method: 'GET',
        headers: {
          'X-Dashboard-Secret': secret,
        },
        // Optional display currency (DKK|EUR); the API defaults it when omitted.
        params: currency ? { currency } : undefined,
      }),
    }),

    getPublicAffiliateStats: builder.query<
      AffiliateStatsDto,
      {
        apiKey: string
        fromDate?: string
        toDate?: string
      }
    >({
      query: ({ apiKey, fromDate, toDate }) => {
        const params = new URLSearchParams()
        if (fromDate) params.append('fromDate', fromDate)
        if (toDate) params.append('toDate', toDate)

        return {
          url: `/affiliate-partners/public-stats${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          headers: {
            'X-Affiliate-API-Key': apiKey,
          },
        }
      },
    }),

    // Public landing-page lookup for a Veli activation link, keyed on the
    // (non-secret) Veli id. An unknown id is a 404 the screen treats as an
    // invalid link.
    getVeliActivationLanding: builder.query<VeliActivationLandingDto, string>({
      query: veliUserId => ({
        url: `/veli-migration/activation/${encodeURIComponent(veliUserId)}`,
        method: 'GET',
      }),
    }),

    // Activate a seeded Veli client: set a password and create the Penning user
    // via the standard pipeline. The email is fixed from the import row.
    activateVeliClient: builder.mutation<UserDto, ActivateVeliClientCommand>({
      query: ({ veliUserId, ...body }) => ({
        url: `/veli-migration/activation/${encodeURIComponent(veliUserId)}`,
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetPlatformDashboardQuery,
  useGetPublicAffiliateStatsQuery,
  useLazyGetPublicAffiliateStatsQuery,
  useGetVeliActivationLandingQuery,
  useActivateVeliClientMutation,
} = publicApi
